// What taking over a trip needs, in words (DESIGN.md, Member, "Taking over a trip"): the same for the member it's
// offered to on the web (People) and for their AI (get_trip's offer, a refused accept). Plain data and words, no
// database, so the web view imports it too. The needs themselves are worked out in src/billing/takeover.ts.

// How long an offer waits for its member before it lapses.
export const OFFER_DAYS = 14;

// What taking over a trip needs of the member it's offered to, worked out as it would be accepted now.
export interface TakeoverNeeds {
  // What pays for the trip once it's theirs:
  // "plan": their own plan (Frequent Flyer, Lifetime, Partner), so nothing more, and no limit on people;
  // "keeps": the trip's own Trip Pass, which stays with it, as its spots do;
  // "unused": their unused Trip Pass, used up by taking it over (the trip is then on it);
  // "buy": a Trip Pass, bought first;
  // "none": nothing, where Tripmo isn't charging for trips (BILLING_ENFORCED off): it stays as it is.
  pass: "plan" | "keeps" | "unused" | "buy" | "none";
  // Their plan's name, when it's what pays.
  plan: string | null;
  // How many are in it now, owner included.
  people: number;
  // How many it could have once theirs, before any spots bought (null: no limit).
  limit: number | null;
  // Blocks of 5 more spots to buy first, so everyone in it fits.
  blocks: number;
  // What's to buy first, in cents: the pass (when "buy") and the blocks.
  cost: number;
  // What a Trip Pass and a block of 5 more spots cost now, in cents.
  prices: { pass: number; block: number };
}

export const SPOTS_PER_BLOCK = 5;

// "Sun 15 Nov 2026": the day an offer lapses, with its weekday, as an AI is told it (every date Tripmo tells an AI says
// its weekday: DESIGN.md section 4). In UTC: the person's own day may be either side of it.
const LAPSE_DAY = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
export const offerDay = (iso: string): string => LAPSE_DAY.format(new Date(iso)).replace(",", "");

// Nothing to buy: it can be accepted as it is.
export const ready = (needs: TakeoverNeeds): boolean => needs.pass !== "buy" && needs.blocks === 0;

// "$5", "$7.49".
export const dollars = (cents: number): string =>
  cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

const blocksOf = (n: number) => `${n} block${n === 1 ? "" : "s"} of ${SPOTS_PER_BLOCK} more spots`;

const both = (parts: string[]) => (parts.length < 2 ? (parts[0] ?? "") : `${parts[0]} and ${parts[1]}`);

// What taking it over uses, in one sentence, the count of each thing said: "Taking it over uses 1 Trip Pass ($7.49)
// and 1 block of 5 more spots ($5), bought first." `priced` false leaves the prices out (a host that forbids naming
// them: src/mcp/paying.ts).
export function takeoverLine(needs: TakeoverNeeds, { priced = true }: { priced?: boolean } = {}): string {
  const price = (cents: number) => (priced ? ` (${dollars(cents)})` : "");
  if (ready(needs)) {
    if (needs.pass === "plan") return `Taking it over needs nothing more: you're on ${needs.plan ?? "a plan"}.`;
    if (needs.pass === "keeps") return "Taking it over needs nothing more: its Trip Pass and spots stay with it.";
    if (needs.pass === "unused") return "Taking it over uses your unused Trip Pass.";
    return "Taking it over needs nothing more.";
  }
  const bought: string[] = [];
  if (needs.pass === "buy") bought.push(`1 Trip Pass${price(needs.prices.pass)}`);
  if (needs.blocks > 0) bought.push(`${blocksOf(needs.blocks)}${price(needs.blocks * needs.prices.block)}`);
  const total = bought.length > 1 && priced ? `: ${dollars(needs.cost)} in all` : "";
  if (needs.pass === "unused") return `Taking it over uses your unused Trip Pass, and ${bought[0]} bought first.`;
  return `Taking it over uses ${both(bought)}, bought first${total}.`;
}

// Why, when there's something to buy: without a plan, a trip needs a Trip Pass of theirs (a trip started under a plan,
// or from before paying, has none of its own), and a trip with more people than they could have needs the spots.
export function takeoverWhy(needs: TakeoverNeeds): string | null {
  if (ready(needs)) return null;
  const why: string[] = [];
  if (needs.pass === "buy" || needs.pass === "unused")
    why.push("Without a plan of your own, it needs a Trip Pass of yours, as every trip does.");
  if (needs.blocks > 0 && needs.limit !== null)
    why.push(`It has ${needs.people} people, owner included, and you'd have ${needs.limit} spots.`);
  return why.length ? why.join(" ") : null;
}

// The order it's paid in, when both a Trip Pass and spots are to be bought first (each its own Checkout): "You pay for
// the Trip Pass first, then the spots ($5): the trip is yours once both are paid." Null otherwise.
export function takeoverSteps(needs: TakeoverNeeds): string | null {
  if (needs.pass !== "buy" || needs.blocks === 0) return null;
  return `You pay for the Trip Pass first, then the spots (${dollars(needs.blocks * needs.prices.block)}): the trip is yours once both are paid.`;
}
